// Central schema definition driving all CRUD pages.
// field.type: text | number | currency | date | textarea | select | entity | computed
// select -> optionsKey (editable dropdown backed by /api/options)
// entity -> link { entity, labelField }

export const ENTITY_SCHEMAS = {
  jobs: {
    title: "Jobs Master",
    singular: "Job",
    icon: "Briefcase",
    idLabel: "Job ID",
    labelField: "job_name",
    fields: [
      { name: "code", label: "Job ID", type: "text", readOnly: true, editableId: true },
      { name: "client_name", label: "Client Name", type: "text", required: true },
      { name: "job_name", label: "Job Name / Description", type: "text", required: true },
      { name: "product_type", label: "Product Type", type: "select", optionsKey: "product_type" },
      { name: "job_status", label: "Job Status", type: "select", optionsKey: "job_status" },
      { name: "machine_plan", label: "Machines · Planned Hours · Operators", type: "machine_plan" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "end_date", label: "End Date (Expected)", type: "date" },
      { name: "budget_allocated", label: "Budget Allocated", type: "currency" },
      { name: "budget_spent", label: "Budget Spent", type: "computed", kind: "currency" },
      { name: "profitability", label: "Profitability %", type: "computed", kind: "percent" },
    ],
  },
  team: {
    title: "Team Members",
    singular: "Team Member",
    icon: "UsersThree",
    idLabel: "Employee ID",
    labelField: "name",
    noCreate: true,
    createHint: "Team Members come from Manage Users — add a login account there and the member appears here automatically.",
    fields: [
      { name: "code", label: "Employee ID", type: "text", readOnly: true, editableId: true },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "role", label: "Role", type: "select", optionsKey: "team_role" },
      { name: "department", label: "Department", type: "select", optionsKey: "department" },
      { name: "hourly_rate", label: "Hourly Rate", type: "currency" },
      { name: "machine_specialization", label: "Machine Specialization", type: "entity", link: { entity: "machines", labelField: "machine_name" } },
    ],
  },
  machines: {
    title: "Machines & Equipment",
    singular: "Machine",
    icon: "Gear",
    idLabel: "Machine ID",
    labelField: "machine_name",
    fields: [
      { name: "code", label: "Machine ID", type: "text", editableId: true },
      { name: "machine_name", label: "Machine Name", type: "text", required: true },
      { name: "machine_type", label: "Machine Type", type: "select", optionsKey: "machine_type" },
      { name: "category", label: "Category", type: "select", optionsKey: "machine_category" },
      { name: "cost_per_hour", label: "Cost Per Hour", type: "currency" },
      { name: "status", label: "Status", type: "select", optionsKey: "machine_status" },
      { name: "last_maintenance_date", label: "Last Maintenance Date", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  operations: {
    title: "Operations Master",
    singular: "Operation",
    icon: "ListChecks",
    idLabel: "Operation ID",
    labelField: "operation_name",
    fields: [
      { name: "code", label: "Operation ID", type: "text", readOnly: true },
      { name: "operation_name", label: "Operation Name", type: "select", optionsKey: "operation_name" },
      { name: "machine_required", label: "Machine Required", type: "entity", link: { entity: "machines", labelField: "machine_name" } },
      { name: "is_external", label: "Is External Process", type: "select", optionsKey: "yes_no" },
      { name: "standard_time_hours", label: "Standard Time (Hours)", type: "number" },
      { name: "standard_cost", label: "Standard Cost", type: "currency" },
    ],
  },
  production: {
    title: "Daily Production Log",
    singular: "Log Entry",
    icon: "ClipboardText",
    idLabel: "Log ID",
    labelField: "code",
    fields: [
      { name: "code", label: "Log ID", type: "text", readOnly: true },
      { name: "date", label: "Date", type: "date", default: "today" },
      { name: "job_id", label: "Job", type: "entity", link: { entity: "jobs", labelField: "job_name" } },
      { name: "machine_id", label: "Machine", type: "entity", link: { entity: "machines", labelField: "machine_name" } },
      { name: "operator_id", label: "Operator", type: "entity", link: { entity: "team", labelField: "name" } },
      { name: "operation_id", label: "Operation", type: "entity", link: { entity: "operations", labelField: "operation_name" } },
      { name: "planned_hours", label: "Planned Hours", type: "number" },
      { name: "actual_hours", label: "Actual Hours", type: "number" },
      { name: "planned_output", label: "Planned Output", type: "number" },
      { name: "actual_output", label: "Actual Output", type: "number" },
      { name: "rework_scrap", label: "Rework / Scrap", type: "number" },
      { name: "downtime", label: "Downtime (Hours)", type: "number" },
      { name: "downtime_reason", label: "Downtime Reason", type: "select", optionsKey: "downtime_reason" },
      { name: "notes", label: "Notes", type: "textarea" },
      { name: "approval_status", label: "Approval", type: "approval", readOnly: true },
    ],
  },
  costs: {
    title: "Job Cost Tracking",
    singular: "Cost Entry",
    icon: "CurrencyInr",
    idLabel: "Cost ID",
    labelField: "code",
    fields: [
      { name: "code", label: "Cost ID", type: "text", readOnly: true },
      { name: "job_id", label: "Job", type: "entity", link: { entity: "jobs", labelField: "job_name" } },
      { name: "cost_category", label: "Cost Category", type: "select", optionsKey: "cost_category" },
      { name: "operation_id", label: "Operation (optional)", type: "entity", link: { entity: "operations", labelField: "operation_name" }, optional: true },
      { name: "planned_cost", label: "Planned Cost", type: "currency" },
      { name: "actual_cost", label: "Actual Cost", type: "currency" },
      { name: "cost_variance", label: "Cost Variance", type: "computed", kind: "currency" },
      { name: "date", label: "Date", type: "date", default: "today" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
  },
  utilization: {
    title: "Machine Utilization",
    singular: "Utilization Entry",
    icon: "ChartLineUp",
    idLabel: "Utilization ID",
    labelField: "code",
    fields: [
      { name: "code", label: "Utilization ID", type: "text", readOnly: true },
      { name: "machine_id", label: "Machine", type: "entity", link: { entity: "machines", labelField: "machine_name" } },
      { name: "date", label: "Date", type: "date", default: "today" },
      { name: "planned_hours", label: "Planned Hours", type: "number" },
      { name: "actual_hours", label: "Actual Hours", type: "number" },
      { name: "utilization_percentage", label: "Utilization %", type: "computed", kind: "percent" },
      { name: "idle_hours", label: "Idle Hours", type: "number" },
      { name: "maintenance_hours", label: "Maintenance Hours", type: "number" },
    ],
  },
  external: {
    title: "External Processes",
    singular: "External Process",
    icon: "Truck",
    idLabel: "Process ID",
    labelField: "process_name",
    fields: [
      { name: "code", label: "Process ID", type: "text", readOnly: true },
      { name: "job_id", label: "Job", type: "entity", link: { entity: "jobs", labelField: "job_name" } },
      { name: "process_name", label: "Process Name", type: "select", optionsKey: "external_process_name" },
      { name: "vendor_name", label: "Vendor Name", type: "text" },
      { name: "planned_cost", label: "Planned Cost", type: "currency" },
      { name: "actual_cost", label: "Actual Cost", type: "currency" },
      { name: "date_sent", label: "Date Sent", type: "date" },
      { name: "date_received", label: "Date Received", type: "date" },
      { name: "status", label: "Status", type: "select", optionsKey: "external_process_status" },
    ],
  },
};

export const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: "SquaresFour", path: "/" },
  { key: "jobs", label: "Jobs Master", icon: "Briefcase", path: "/jobs" },
  { key: "team", label: "Team Members", icon: "UsersThree", path: "/team" },
  { key: "machines", label: "Machines & Equipment", icon: "Gear", path: "/machines" },
  { key: "operations", label: "Operations Master", icon: "ListChecks", path: "/operations" },
  { key: "production", label: "Daily Production Log", icon: "ClipboardText", path: "/production" },
  { key: "costs", label: "Job Cost Tracking", icon: "CurrencyInr", path: "/costs" },
  { key: "utilization", label: "Machine Utilization", icon: "ChartLineUp", path: "/utilization" },
  { key: "load", label: "Machine Load", icon: "CalendarBlank", path: "/machine-load" },
  { key: "external", label: "External Processes", icon: "Truck", path: "/external" },
  { key: "reports", label: "Reports", icon: "FileText", path: "/reports" },
];
